import { ToastContext } from "context/ToastContext";
import { CONTRACT_TYPE_OPTIONS } from "pages/PayerRules/contracts/helpers";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useEffect, useState } from "react";
import {
  IContractModelingInstructionEntry,
  IContractModelingInstructionVersion,
  useGetContractModelingInstructionsQuery,
  useSaveContractModelingInstructionMutation,
} from "store/queries/contractModelDrafts";

import { instructionExplanation, STANDING_INSTRUCTIONS_INTRO } from "./copy";
import {
  instructionVersionLine,
  isInstructionDirty,
} from "./standingInstructions";

const typeTitle = (contractType: string): string =>
  CONTRACT_TYPE_OPTIONS.find((o) => o.value === contractType)?.label ??
  contractType;

/** Mirrors the API's cap (SaveContractModelingInstructionDto). */
const MAX_LENGTH = 20000;

function InstructionCard({
  entry,
  onView,
}: {
  entry: IContractModelingInstructionEntry;
  onView: (v: IContractModelingInstructionVersion) => void;
}) {
  const toastRef = useContext(ToastContext);
  const [save, { isLoading: saving }] =
    useSaveContractModelingInstructionMutation();
  const [draft, setDraft] = useState(entry.latest?.body ?? "");
  const [showHistory, setShowHistory] = useState(false);

  // A save (here or by someone else, on refetch) moves the saved version: start from it again.
  useEffect(() => {
    setDraft(entry.latest?.body ?? "");
  }, [entry.latest?.id, entry.latest?.body]);

  const dirty = isInstructionDirty(draft, entry.latest);

  const handleSave = async () => {
    try {
      const saved = await save({
        contractType: entry.contractType,
        body: draft,
      }).unwrap();
      toastRef?.current?.show({
        severity: "success",
        summary: `${entry.contractType} instructions saved`,
        detail: saved.latest ? `Now version ${saved.latest.version}.` : "",
        life: 4000,
      });
    } catch (e) {
      const apiMessage = (e as { data?: { message?: string } })?.data?.message;
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't save",
        detail:
          typeof apiMessage === "string" && apiMessage
            ? apiMessage
            : "Something went wrong. Please try again.",
        life: 7000,
      });
    }
  };

  return (
    <Card title={typeTitle(entry.contractType)} className="mb-3">
      <p className="text-600 mt-0 mb-2">
        {instructionExplanation(entry.contractType)}
      </p>
      <InputTextarea
        className="w-full"
        autoResize
        rows={6}
        maxLength={MAX_LENGTH}
        value={draft}
        placeholder="No standing instructions for this contract type."
        aria-label={`Standing instructions for ${entry.contractType} contracts`}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex justify-content-between align-items-center mt-2 gap-3">
        <small className="text-500">
          {entry.latest
            ? `${instructionVersionLine(entry.latest)}${
                entry.latest.body ? "" : " (cleared)"
              }`
            : "Never saved"}
        </small>
        <div className="flex gap-2">
          {dirty && (
            <Button
              label="Discard"
              className="p-button-text p-button-sm"
              disabled={saving}
              onClick={() => setDraft(entry.latest?.body ?? "")}
            />
          )}
          <Button
            label="Save"
            icon="pi pi-check"
            className="p-button-sm"
            disabled={!dirty || saving}
            loading={saving}
            onClick={handleSave}
          />
        </div>
      </div>
      {entry.history.length > 0 && (
        <div className="mt-3">
          <Button
            label={`History (${entry.history.length})`}
            icon={showHistory ? "pi pi-chevron-down" : "pi pi-chevron-right"}
            className="p-button-text p-button-sm p-0"
            onClick={() => setShowHistory((s) => !s)}
          />
          {showHistory && (
            <ul className="list-none p-0 m-0 mt-2">
              {entry.history.map((v) => (
                <li
                  key={v.id}
                  className="flex justify-content-between align-items-center py-1 border-bottom-1 surface-border"
                >
                  <span className="text-600 text-sm">
                    {instructionVersionLine(v)}
                    {v.body ? "" : " (cleared)"}
                  </span>
                  <Button
                    label="View"
                    className="p-button-text p-button-sm"
                    onClick={() => onView(v)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * CLE-2811 — the "Standing instructions" tab: one card per contract type. What is saved here is added
 * to every AI run on a contract of that type (snapshotted when the run is queued). Append-only: each
 * save is a new version and the older ones stay readable under History.
 */
export function StandingInstructionsTab() {
  const { data: entries, isLoading } =
    useGetContractModelingInstructionsQuery();
  const [viewing, setViewing] = useState<{
    contractType: string;
    version: IContractModelingInstructionVersion;
  } | null>(null);

  if (isLoading) return <ProgressSpinner />;

  return (
    <div>
      <p className="text-600 mt-0 mb-3">{STANDING_INSTRUCTIONS_INTRO}</p>
      {(entries ?? []).map((entry) => (
        <InstructionCard
          key={entry.contractType}
          entry={entry}
          onView={(version) =>
            setViewing({ contractType: entry.contractType, version })
          }
        />
      ))}
      <Dialog
        header={
          viewing
            ? `${viewing.contractType} — ${instructionVersionLine(
                viewing.version
              )}`
            : ""
        }
        visible={!!viewing}
        style={{ width: "48rem", maxWidth: "95vw" }}
        onHide={() => setViewing(null)}
      >
        {viewing && (
          <pre
            className="white-space-pre-wrap m-0 text-sm line-height-3"
            style={{ fontFamily: "inherit" }}
          >
            {viewing.version.body || "(cleared — no instructions)"}
          </pre>
        )}
      </Dialog>
    </div>
  );
}
